import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";

/** HTTP API for Nav2 params YAML on the robot (nav_config_api.py), default port 12406. */

export type Nav2ParamsProfile = {
    name: string;
    /** Logical profile name for the active file (e.g. diffdrive), when location is active. */
    profile_name?: string | null;
    location: "active" | "profiles";
    is_active: boolean;
    mtime: number;
};

export type Nav2ParamsActiveResponse = {
    name: string;
    /** Logical profile name backing the active nav2_params.yaml file. */
    profile_name?: string | null;
    path: string;
    content: string;
    mtime: number;
};

export type Nav2ParamsProfilesResponse = {
    profiles: Nav2ParamsProfile[];
    params_dir: string;
};

export type Nav2NavStatusResponse = {
    service: string;
    active_state: string;
    sub_state: string;
    /** Nav2 lifecycle stack is up (managed nodes active). */
    running: boolean;
    /** systemd reports nav.service as active. */
    service_active?: boolean;
    /** Same as running; explicit Nav2 bringup-complete signal. */
    ready?: boolean;
};

export type NavServiceDisplayState = "unknown" | "stopped" | "starting" | "running";

export function navServiceDisplayState(
    status: Nav2NavStatusResponse | null
): NavServiceDisplayState {
    if (!status) {
        return "unknown";
    }
    const serviceActive = status.service_active ?? status.active_state === "active";
    const ready = status.ready ?? status.running;
    if (!serviceActive) {
        return "stopped";
    }
    if (ready) {
        return "running";
    }
    if (status.active_state === "active" && status.sub_state === "running") {
        return "running";
    }
    return "starting";
}

export type Nav2HealthResponse = {
    ok: boolean;
    service: string;
    port: number;
    params_dir: string;
};

export type Nav2OkResponse = {
    ok: boolean;
    message?: string;
    backup?: string | null;
    name?: string;
    activated?: string;
    profile_name?: string;
    synced_profile?: string | null;
    content?: string;
};

export type Nav2OperatorParamsResponse = {
    ok: boolean;
    profile_name?: string | null;
    vx_max: number;
    local_cost_scaling_factor: number;
    global_cost_scaling_factor: number;
    local_inflation_radius: number;
    global_inflation_radius: number;
    /** @deprecated Prefer local_cost_scaling_factor; kept for older API responses. */
    cost_scaling_factor?: number;
    limits?: {
        vx_max: { min: number; max: number };
        cost_scaling_factor?: { min: number; max: number };
        local_cost_scaling_factor?: { min: number; max: number };
        global_cost_scaling_factor?: { min: number; max: number };
        local_inflation_radius?: { min: number; max: number };
        global_inflation_radius?: { min: number; max: number };
    };
};

export type Nav2OperatorParamsPatch = {
    vx_max?: number;
    local_cost_scaling_factor?: number;
    global_cost_scaling_factor?: number;
    local_inflation_radius?: number;
    global_inflation_radius?: number;
};

export type Nav2OperatorParamsPatchResponse = Nav2OperatorParamsResponse & {
    backup?: string | null;
    synced_profile?: string | null;
    message?: string;
    nav_restarted?: boolean;
    live_applied?: boolean;
    live_errors?: string[];
};

export type CollisionPolygonBounds = {
    x_min: number;
    x_max: number;
    y_min: number;
    y_max: number;
};

export type CollisionPolygonInfo = {
    enabled: boolean;
    action_type: string;
    max_points: number;
    points: number[];
    bounds: CollisionPolygonBounds;
};

export type Nav2CollisionMonitorResponse = {
    ok: boolean;
    enabled: boolean;
    path?: string;
    polygons?: Record<string, CollisionPolygonInfo>;
    footprint?: number[][] | null;
};

export type Nav2CollisionMonitorPatch = {
    enabled?: boolean;
    polygons?: Record<string, { bounds: CollisionPolygonBounds }>;
};

export type Nav2CollisionMonitorPatchResponse = Nav2CollisionMonitorResponse & {
    nav_service?: {
        active_state: string;
        sub_state: string;
        service_active: boolean;
        restart_scheduled: boolean;
    };
    message?: string;
};

export type Nav2BehaviorActionType =
    | "clear_local_costmap"
    | "clear_global_costmap"
    | "wait"
    | "set_collision_monitor"
    | "retry_planner"
    | "alternate_planner"
    | "retry_controller"
    | "recovery_controller"
    | "backup";

export type Nav2BehaviorAction = {
    id: string;
    type: Nav2BehaviorActionType;
    on_failure: "continue" | "stop";
    params: Record<string, string | number | boolean>;
};

export type Nav2BehaviorStage = {
    enabled: boolean;
    primary_id: string | null;
    actions: Nav2BehaviorAction[];
};

export type Nav2BehaviorTreeSettings = {
    bt_template?: "managed" | "classic";
    replan_rate_hz: number;
    navigate_recovery_retries: number;
    planning: Nav2BehaviorStage;
    controller: Nav2BehaviorStage;
    recovery: Nav2BehaviorStage;
};

export type Nav2BehaviorTreeLimits = Record<string, { min: number; max: number }>;

export type Nav2BehaviorTreeCatalog = {
    stage_actions: {
        planning: Nav2BehaviorActionType[];
        controller: Nav2BehaviorActionType[];
        recovery: Nav2BehaviorActionType[];
    };
    planner_ids: string[];
    controller_ids: string[];
    failure_policies: Array<"continue" | "stop">;
};

export type Nav2NavigationBehaviorResponse = {
    ok: boolean;
    profile_name?: string | null;
    behavior_profile_name?: string | null;
    scope: "navigate_to_pose";
    navigate_through_poses_unchanged: boolean;
    bt_xml_path?: string | null;
    settings: Nav2BehaviorTreeSettings;
    defaults: Nav2BehaviorTreeSettings;
    limits: Nav2BehaviorTreeLimits;
    catalog: Nav2BehaviorTreeCatalog;
    requires_restart: boolean;
};

export type Nav2NavigationBehaviorPatch = Nav2BehaviorTreeSettings;

export type Nav2BehaviorLiveStage = {
    ok: boolean;
    current_stage: string | null;
    timestamp: string;
    nav_service: {
        active_state: string;
        sub_state: string;
        service_active: boolean;
    };
};

export type Nav2NavigationBehaviorPatchResponse = Nav2NavigationBehaviorResponse & {
    backup?: string | null;
    synced_profile?: string | null;
    nav_service?: {
        active_state: string;
        sub_state: string;
        service_active: boolean;
        restart_scheduled: boolean;
    };
    message?: string;
};

export type Nav2BehaviorProfile = {
    name: string;
    is_active: boolean;
    mtime: number;
};

export type Nav2BehaviorProfilesResponse = {
    ok: boolean;
    profiles: Nav2BehaviorProfile[];
    active: string | null;
    profiles_dir?: string;
};

export type Nav2BehaviorProfileResponse = {
    ok: boolean;
    name: string;
    settings: Nav2BehaviorTreeSettings;
    is_active: boolean;
    defaults?: Nav2BehaviorTreeSettings;
    limits?: Nav2BehaviorTreeLimits;
    catalog?: Nav2BehaviorTreeCatalog;
};

export type Nav2BehaviorProfileWriteResponse = {
    ok: boolean;
    name: string;
    settings: Nav2BehaviorTreeSettings;
    is_active: boolean;
    applied_live?: boolean;
    bt_xml_path?: string | null;
    backup?: string | null;
    synced_profile?: string | null;
    nav_service?: {
        active_state: string;
        sub_state: string;
        service_active: boolean;
        restart_scheduled: boolean;
    };
    message?: string;
    requires_restart?: boolean;
};

const ACTIVE_PROFILE_FILE = "nav2_params.yaml";

export function profileNameStem(fileName: string): string {
    return fileName.replace(/\.yaml$/i, "");
}

export function profileFileName(stemOrFile: string): string {
    return /\.yaml$/i.test(stemOrFile) ? stemOrFile : `${stemOrFile}.yaml`;
}

export function isActiveFileEntry(profile: Nav2ParamsProfile): boolean {
    return profile.is_active || profile.location === "active";
}

export function storedProfiles(profiles: Nav2ParamsProfile[]): Nav2ParamsProfile[] {
    return profiles.filter((profile) => !isActiveFileEntry(profile));
}

export function resolveActiveProfileName(
    profiles: Nav2ParamsProfile[],
    activeResponse?: Pick<Nav2ParamsActiveResponse, "profile_name" | "mtime"> | null
): string | null {
    const activeEntry = profiles.find(isActiveFileEntry);
    if (activeEntry?.profile_name) {
        return activeEntry.profile_name;
    }
    if (activeResponse?.profile_name) {
        return activeResponse.profile_name;
    }
    const activeMtime = activeEntry?.mtime ?? activeResponse?.mtime;
    if (activeMtime !== undefined) {
        const byMtime = profiles.find(
            (profile) => !isActiveFileEntry(profile) && profile.mtime === activeMtime
        );
        if (byMtime) {
            return profileNameStem(byMtime.name);
        }
    }
    return null;
}

export function isSelectedProfileActive(
    selectedName: string,
    activeProfileName: string | null
): boolean {
    if (!activeProfileName) {
        return false;
    }
    return profileNameStem(selectedName) === activeProfileName;
}

export function selectionForActiveProfile(
    profiles: Nav2ParamsProfile[],
    activeProfileName: string | null
): string {
    if (!activeProfileName) {
        return ACTIVE_PROFILE_FILE;
    }
    const profileFile = profileFileName(activeProfileName);
    return profiles.some((profile) => profile.name === profileFile)
        ? profileFile
        : ACTIVE_PROFILE_FILE;
}

export { ACTIVE_PROFILE_FILE };

const DEFAULT_NAV2_PARAMS_API_PORT = "12406";

function parseFastApiDetail(body: { detail?: unknown }): string | undefined {
    const d = body?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0] && typeof (d[0] as { msg?: string }).msg === "string") {
        return (d[0] as { msg: string }).msg;
    }
    return undefined;
}

async function parseError(res: Response): Promise<string> {
    let detail = `HTTP ${res.status}`;
    try {
        const body = (await res.json()) as { detail?: unknown };
        const d = parseFastApiDetail(body);
        if (d) detail = d;
    } catch {
        /* ignore */
    }
    return detail;
}

/** Base URL with no trailing slash. */
export function getNav2ParamsApiBaseUrl(
    selectedArcHost?: string | null,
    isSecure: boolean = isHttpSecure(),
    arcId?: string | null
): string | null {
    if (process.env.REACT_APP_NAV2_PARAMS_API_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_NAV2_PARAMS_API_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port = process.env.REACT_APP_NAV2_PARAMS_API_PORT?.trim() || DEFAULT_NAV2_PARAMS_API_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_NAV2_PARAMS_API_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("nav2", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

export async function fetchNav2Health(baseUrl: string): Promise<Nav2HealthResponse> {
    const paths = ["/nav2/health", "/health", "/"];
    let lastDetail = "nav_config_api unreachable";
    for (const path of paths) {
        const res = await authedFetch(joinUrl(baseUrl, path));
        if (!res.ok) {
            lastDetail = res.status === 404
                ? "nav_config_api not found on port 12406 (check nav_config_api.service is running)"
                : await parseError(res);
            continue;
        }
        try {
            const body = (await res.json()) as Nav2HealthResponse;
            if (body.service === "nav_config_api" && body.ok) {
                return body;
            }
            lastDetail = `Unexpected service on port 12406: ${body.service ?? "unknown"}`;
        } catch {
            lastDetail = "Port 12406 is not serving nav_config_api JSON";
        }
    }
    throw new Error(lastDetail);
}

export async function fetchNav2ParamsActive(baseUrl: string): Promise<Nav2ParamsActiveResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/active"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2ParamsActiveResponse>;
}

export async function fetchNav2ParamsProfiles(baseUrl: string): Promise<Nav2ParamsProfilesResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/profiles"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2ParamsProfilesResponse>;
}

export async function fetchNav2ParamsProfile(
    baseUrl: string,
    profileName: string
): Promise<Nav2ParamsActiveResponse> {
    const res = await authedFetch(joinUrl(baseUrl, `/nav2/params/profiles/${encodeURIComponent(profileName)}`));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2ParamsActiveResponse>;
}

export async function putNav2ParamsActive(baseUrl: string, content: string): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/active"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function putNav2ParamsNamedProfile(
    baseUrl: string,
    profileName: string,
    content: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, `/nav2/params/profiles/${encodeURIComponent(profileName)}`),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function postNav2ParamsProfile(
    baseUrl: string,
    name: string,
    content: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/profiles"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, content }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function postNav2ParamsActivate(baseUrl: string, name: string): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/activate"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function postNav2ParamsRename(
    baseUrl: string,
    fromName: string,
    toName: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/rename"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from: fromName, to: toName }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function deleteNav2ParamsProfile(
    baseUrl: string,
    profileName: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/delete"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: profileName }),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function postNav2ParamsRestoreDefault(baseUrl: string): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/restore-default"), {
        method: "POST",
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function fetchNav2NavStatus(baseUrl: string): Promise<Nav2NavStatusResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/nav/status"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2NavStatusResponse>;
}

export async function postNav2NavRestart(baseUrl: string): Promise<Nav2OkResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/nav/restart"), { method: "POST" });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function fetchNav2OperatorParams(
    baseUrl: string
): Promise<Nav2OperatorParamsResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/operator"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OperatorParamsResponse>;
}

export async function patchNav2OperatorParams(
    baseUrl: string,
    patch: Nav2OperatorParamsPatch
): Promise<Nav2OperatorParamsPatchResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/operator"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OperatorParamsPatchResponse>;
}

export async function fetchNav2CollisionMonitor(
    baseUrl: string
): Promise<Nav2CollisionMonitorResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/collision-monitor"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2CollisionMonitorResponse>;
}

export async function patchNav2CollisionMonitor(
    baseUrl: string,
    patch: Nav2CollisionMonitorPatch | boolean
): Promise<Nav2CollisionMonitorPatchResponse> {
    const body: Nav2CollisionMonitorPatch =
        typeof patch === "boolean" ? { enabled: patch } : patch;
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/collision-monitor"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2CollisionMonitorPatchResponse>;
}

export async function fetchNav2NavigationBehavior(
    baseUrl: string
): Promise<Nav2NavigationBehaviorResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/navigation-behavior"));
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2NavigationBehaviorResponse>;
}

export async function fetchNav2NavigationBehaviorLiveStage(
    baseUrl: string
): Promise<Nav2BehaviorLiveStage> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/live-stage")
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorLiveStage>;
}

export async function patchNav2NavigationBehavior(
    baseUrl: string,
    patch: Nav2NavigationBehaviorPatch
): Promise<Nav2NavigationBehaviorPatchResponse> {
    const res = await authedFetch(joinUrl(baseUrl, "/nav2/params/navigation-behavior"), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2NavigationBehaviorPatchResponse>;
}

export async function fetchNav2BehaviorProfiles(
    baseUrl: string
): Promise<Nav2BehaviorProfilesResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/profiles")
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorProfilesResponse>;
}

export async function fetchNav2BehaviorProfile(
    baseUrl: string,
    name: string
): Promise<Nav2BehaviorProfileResponse> {
    const res = await authedFetch(
        joinUrl(
            baseUrl,
            `/nav2/params/navigation-behavior/profiles/${encodeURIComponent(name)}`
        )
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorProfileResponse>;
}

export async function putNav2BehaviorProfile(
    baseUrl: string,
    name: string,
    settings: Nav2BehaviorTreeSettings
): Promise<Nav2BehaviorProfileWriteResponse> {
    const res = await authedFetch(
        joinUrl(
            baseUrl,
            `/nav2/params/navigation-behavior/profiles/${encodeURIComponent(name)}`
        ),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(settings),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorProfileWriteResponse>;
}

export async function postNav2BehaviorProfile(
    baseUrl: string,
    name: string,
    settings: Nav2BehaviorTreeSettings
): Promise<Nav2BehaviorProfileWriteResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/profiles"),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name, settings }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorProfileWriteResponse>;
}

export async function postNav2BehaviorActivate(
    baseUrl: string,
    name: string
): Promise<Nav2NavigationBehaviorPatchResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/activate"),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2NavigationBehaviorPatchResponse>;
}

export async function postNav2BehaviorRename(
    baseUrl: string,
    fromName: string,
    toName: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/rename"),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ from: fromName, to: toName }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export async function deleteNav2BehaviorProfile(
    baseUrl: string,
    name: string
): Promise<Nav2OkResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/delete"),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2OkResponse>;
}

export type Nav2BehaviorTreeXmlResponse = {
    ok: boolean;
    exists: boolean;
    path: string;
    content: string;
    behavior_profile_name?: string | null;
    settings_json_path?: string;
    requires_restart: boolean;
    warning?: string | null;
};

export type Nav2BehaviorTreeXmlPutResponse = {
    ok: boolean;
    path: string;
    backup?: string | null;
    synced_profile?: string | null;
    behavior_profile_name?: string | null;
    requires_restart: boolean;
    settings_json_stale?: boolean;
    message?: string;
    nav_service?: {
        active_state: string;
        sub_state: string;
        service_active: boolean;
        restart_scheduled: boolean;
    };
};

export async function fetchNav2BehaviorTreeXml(
    baseUrl: string
): Promise<Nav2BehaviorTreeXmlResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/bt-xml")
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorTreeXmlResponse>;
}

export async function putNav2BehaviorTreeXml(
    baseUrl: string,
    content: string
): Promise<Nav2BehaviorTreeXmlPutResponse> {
    const res = await authedFetch(
        joinUrl(baseUrl, "/nav2/params/navigation-behavior/bt-xml"),
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content }),
        }
    );
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<Nav2BehaviorTreeXmlPutResponse>;
}
